import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { Check, Close } from "@similarweb/icons";
import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Box, Text } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../../app/navigation";
import { useGetTrademarkAds, useMonitorAndProtectTrademarkAdExcelDownload } from "../../../services/api/trademarks";
import { trademarksTracking } from "../../../services/tracking/trademarks";
import MetricCard from "../../../shared/components/metric-card/metric-card";
import { MetricCardContainer } from "../../../shared/components/metric-card/metric-card.styled";
import { DEFAULT_MAX_EXCEL_ROWS } from "../../../shared/data/component-config/component-config";
import { useMonitorAndProtectTrademarkDataAndParams } from "../../../shared/hooks/trademark-params";
import { Widget } from "../../../shared/ui/layout/widget";
import { getNoData } from "../../../shared/ui/table/get-no-data";
import { useDomainFilterQuickApply } from "../filtres/hooks";

import { createConfigBuilder, getParentRowBuilder } from "./table-config";
import type { IExampleTableRow } from "./table-config";

interface IAdKeywordsTableProps {
    adItem?: IExampleTableRow;
    onClose?: () => void;
}

export const TrademarkAdsTable: FC<IAdKeywordsTableProps> = () => {
    const translate = useTranslation();
    const { toggleInclude, toggleExclude } = useDomainFilterQuickApply();
    const { downloadExcel, isLoading: isLoadingExcel } = useMonitorAndProtectTrademarkAdExcelDownload();
    const { sortBy, sortDir, dataFilters } = useMonitorAndProtectTrademarkDataAndParams();
    const [{ pageSize, current }, setPaginationData] = useState<ITableProps["pagination"]>({
        pageSize: 100,
        current: 1,
    });
    const tableErrorType = useMemo(() => {
        if (!dataFilters?.campaignsIds?.length) {
            return "requireCampaigns";
        }
        if (dataFilters?.campaignsIds?.length > 10) {
            return "campaignLimitReached";
        }
        return undefined;
    }, [dataFilters]);

    const {
        data: trademarkAds,
        isLoading: trademarkAdsIsLoading,
        isError: trademarkAdsIsError,
    } = useGetTrademarkAds(
        {
            pageSize: pageSize,
            page: current,
        },
        !!tableErrorType,
    );

    const onExcelClick = useCallback(() => {
        trademarksTracking.onExcelDownload();
        void downloadExcel()
            .then(() => {
                return {
                    success: true,
                    message: "",
                };
            })
            .catch((error: Error) => {
                return {
                    success: false,
                    message:
                        error.message ||
                        translate("monitor_and_protect.features.trademark_dashboard.trademark_table.excel_error"),
                };
            });
    }, [downloadExcel, translate]);

    const handleSorting: ITableProps<IExampleTableRow>["onSort"] = (sorter) => {
        mnpTrademarkNavigation.updateQueryParams({
            sortBy: sorter.field as string,
            sortDir: sorter.order,
        });
    };

    const tableConfig = useMemo(() => {
        return createConfigBuilder({ pageSize, current, total: trademarkAds?.totalRecords ?? 0 }, translate)
            .setControlledSort(
                {
                    columnKey: sortBy,
                    order: sortDir,
                },
                handleSorting,
            )
            .setPagination({
                pageSize,
                current,
                total: trademarkAds?.totalRecords ?? 0,
                onChange: (_currentPage: number, _pageSize: number, nextPage: number) => {
                    setPaginationData((prev) => ({ ...prev, current: nextPage }));
                },
            })
            .build();
    }, [current, trademarkAds?.totalRecords, pageSize, sortBy, sortDir, translate]);

    const dataFormatted = useMemo(() => {
        return getParentRowBuilder({
            pagination: tableConfig.pagination,
            translateFn: translate,
            advertiserHoverActions: [
                {
                    label: (
                        <>
                            <Check size="xs" />
                            <span>{translate("monitor_and_protect.page.ads.table.domain.hover_action.include")}</span>
                        </>
                    ),
                    onClick: toggleInclude,
                },
                {
                    label: (
                        <>
                            <Close size="xs" />
                            <span>{translate("monitor_and_protect.page.ads.table.domain.hover_action.exclude")}</span>
                        </>
                    ),
                    onClick: toggleExclude,
                },
            ],
        }).transformData(trademarkAds?.records ?? []);
    }, [tableConfig.pagination, trademarkAds?.records, translate, toggleInclude, toggleExclude]);

    return (
        <>
            <Widget width="100%">
                <MetricCardContainer>
                    <MetricCard
                        title={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.ads_analyzed",
                        )}
                        isLoading={trademarkAdsIsLoading}
                        value={(trademarkAds?.totalAnalyzed ?? 0).toLocaleString()}
                        tooltip={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.ads_analyzed_tooltip",
                        )}
                    />
                    <MetricCard
                        title={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.infringed_ads",
                        )}
                        value={(trademarkAds?.totalRecords ?? 0).toLocaleString()}
                        isLoading={trademarkAdsIsLoading}
                        tooltip={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.infringed_ads_tooltip",
                        )}
                    />
                    <MetricCard
                        title={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.advertisers_detected",
                        )}
                        value={(trademarkAds?.uniqueAdvertisersCount ?? 0).toLocaleString()}
                        isLoading={trademarkAdsIsLoading}
                        tooltip={translate(
                            "monitor_and_protect.features.trademark_dashboard.trademark_table.advertisers_detected_tooltip",
                        )}
                    />
                </MetricCardContainer>
            </Widget>
            <Widget
                width="100%"
                data-automation="trademark-table-container"
            >
                <Widget.Header>
                    <Text fontVariant="h5_2">
                        {translate("monitor_and_protect.features.trademark_dashboard.trademark_table.title")}
                    </Text>
                    <Box display={dataFormatted?.length > 0 ? undefined : "none"}>
                        <Tooltip
                            content={translate("monitor_and_protect.features.ads_report.excel_download_tooltip", {
                                maxRows: DEFAULT_MAX_EXCEL_ROWS.toLocaleString(),
                            })}
                            placement="top"
                        >
                            <IconButton
                                iconName="excel"
                                isLoading={isLoadingExcel}
                                type="flat"
                                iconSize="sm"
                                onClick={onExcelClick}
                            />
                        </Tooltip>
                    </Box>
                </Widget.Header>
                <Table
                    selection={{
                        enabled: false,
                    }}
                    isLoading={trademarkAdsIsLoading}
                    noData={getNoData(trademarkAdsIsError, "trademarks", translate, tableErrorType)}
                    sticky={{
                        offsetHeader: -32,
                    }}
                    onSort={tableConfig.onSort}
                    rowKey="id"
                    pagination={tableConfig.pagination}
                    columnConfig={tableConfig.columns}
                    data={dataFormatted}
                />
            </Widget>
        </>
    );
};
